import React, { createContext, useContext, useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Warning } from "@mui/icons-material";
import { COLOR } from "src/components/Styles/Variables/Variables";
import { deleteSession } from "src/slices/user";
import { useDispatch } from "react-redux";
import { useAuth0 } from "@auth0/auth0-react";

const IdleContext = createContext(null);

export const IdleHandleProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { logout, user } = useAuth0();

  const sessionStorage = JSON.parse(localStorage.getItem("sessionStorage"));

  const [currentState, setCurrentState] = useState("active");

  const forceLogout = async (): Promise<void> => {
    try {
      const sessionIds = {
        session_id: sessionStorage?._id,
      };
      let res = await dispatch<any>(deleteSession({ sessionIds: sessionIds }));
      if (res?.error) {
        return;
      } else {
        logout({
          logoutParams: {
            returnTo: process.env.REACT_APP_AUTH0_LOGOUT_REDIRECT_URI,
          },
        });
        localStorage.clear();
      }
    } catch (err) {
      console.log("Error during force logout:", err);
    }
  };

  const onIdle = () => setCurrentState("idle");
  const onActive = () => setCurrentState("active");
  const onPrompt = () => setCurrentState("prompted");

  const keepSignIn = () => activate();
  const logOut = user ? forceLogout : () => {};

  useEffect(() => {
    if (currentState === "idle" && !!user) {
      forceLogout();
    }
  }, [currentState]);

  const { activate } = useIdleTimer({
    onIdle,
    onPrompt,
    onActive,
    promptBeforeIdle: 2 * 60 * 1000,
    timeout: 30 * 60 * 1000,
    crossTab: true,
    leaderElection: true,
    syncTimers: 200,
  });

  return (
    <IdleContext.Provider value={{ currentState, keepSignIn, logOut }}>
      {children}
      <Dialog open={currentState === "prompted" && !!user}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Warning color="error" />
          <Typography variant="h6" textAlign="center" ml={2}>
            Session Time Out
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your session will be logged out in 2 minutes, click OK if you want
            to continue this session.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            sx={{
              outline: "#C8CAD0",
              border: "1px solid #C8CAD0",
              color: "#4D515D",
              backgroundColor: COLOR.white,
              "&:hover": {
                outline: "#C8CAD0",
                border: "1px solid #C8CAD0",
                backgroundColor: COLOR.white,
              },
            }}
            onClick={logOut}
          >
            Log out
          </Button>
          <Button
            variant="outlined"
            sx={{
              backgroundColor: "#F9DB6C",
              color: "#1A1B23",
              border: "1px solid #F9DB6C",
              "&:hover": {
                color: "#1A1B23",
                backgroundColor: "#F9DB6C",
                border: "1px solid #F9DB6C",
              },
            }}
            onClick={keepSignIn}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </IdleContext.Provider>
  );
};

export const useIdle = () => useContext(IdleContext);
